// Acesso direto à RAM do GBA emulado.
//
// O núcleo do mGBA (libretro, compilado para WASM) não expõe leitura/escrita de
// memória, mas a memória do WebAssembly está acessível pelo módulo Emscripten.
// Dentro dela, o mGBA guarda `struct GBAMemory`, que começa com ponteiros
// { bios, wram, iwram, rom } — e wram/iwram ficam num bloco contíguo:
// iwram == wram + 256 KiB. Procuramos esse padrão para achar a EWRAM.

const EWRAM_SIZE = 0x40000; // 256 KiB, mapeada em 0x02000000
const IWRAM_SIZE = 0x8000; // 32 KiB, mapeada em 0x03000000

/** Devolve o buffer atual da memória do WASM (ele é trocado quando a memória cresce). */
export type MemorySource = () => ArrayBufferLike;

export class GbaRam {
  private buffer: ArrayBufferLike | null = null;
  private u8: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
  private view: DataView<ArrayBufferLike> = new DataView(new ArrayBuffer(0));

  constructor(
    private readonly memory: MemorySource,
    /** Offset da EWRAM dentro da memória do WASM. */
    readonly ewramBase: number,
  ) {}

  private offset(addr: number): number {
    const buffer = this.memory();
    if (buffer !== this.buffer) {
      // A memória cresceu: os endereços continuam valendo, só o buffer é outro.
      this.buffer = buffer;
      this.u8 = new Uint8Array(buffer);
      this.view = new DataView(buffer);
    }
    const region = addr >>> 24;
    if (region === 0x02) return this.ewramBase + (addr & (EWRAM_SIZE - 1));
    if (region === 0x03) return this.ewramBase + EWRAM_SIZE + (addr & (IWRAM_SIZE - 1));
    throw new RangeError(`Endereço fora da RAM do GBA: 0x${addr.toString(16)}`);
  }

  // Sempre calcular o offset antes de tocar em u8/view: offset() pode trocar as visões
  // (memória cresceu), e `this.view.getX(this.offset())` usaria a visão antiga.
  u8at(addr: number): number {
    const at = this.offset(addr);
    return this.u8[at];
  }
  s8(addr: number): number {
    const at = this.offset(addr);
    return this.view.getInt8(at);
  }
  u16(addr: number): number {
    const at = this.offset(addr);
    return this.view.getUint16(at, true);
  }
  s16(addr: number): number {
    const at = this.offset(addr);
    return this.view.getInt16(at, true);
  }
  u32(addr: number): number {
    const at = this.offset(addr);
    return this.view.getUint32(at, true);
  }
  bytes(addr: number, length: number): Uint8Array {
    const at = this.offset(addr);
    return this.u8.slice(at, at + length);
  }

  write8(addr: number, value: number): void {
    const at = this.offset(addr);
    this.u8[at] = value;
  }
  write16(addr: number, value: number): void {
    const at = this.offset(addr);
    this.view.setUint16(at, value, true);
  }
  write32(addr: number, value: number): void {
    const at = this.offset(addr);
    this.view.setUint32(at, value, true);
  }
  writeBytes(addr: number, data: Uint8Array): void {
    const at = this.offset(addr);
    this.u8.set(data, at);
  }
}

/**
 * Procura a `struct GBAMemory` do mGBA na memória do WASM.
 * `romHeader` são os bytes 0xA0..0xB0 da ROM carregada, usados para confirmar
 * que o ponteiro `rom` aponta para o jogo certo.
 */
export function locateGbaRam(memory: MemorySource, romHeader: Uint8Array): GbaRam | null {
  const buffer = memory();
  const u32 = new Uint32Array(buffer);
  const u8 = new Uint8Array(buffer);
  const limit = buffer.byteLength;

  const romMatches = (romPtr: number): boolean => {
    const at = romPtr + 0xa0;
    if (romPtr === 0 || (romPtr & 3) !== 0 || at + romHeader.length > limit) return false;
    for (let i = 0; i < romHeader.length; i++) {
      if (u8[at + i] !== romHeader[i]) return false;
    }
    return true;
  };

  for (let i = 1, n = u32.length - 2; i < n; i++) {
    const wram = u32[i];
    if (wram === 0 || u32[i + 1] !== wram + EWRAM_SIZE) continue;
    if ((wram & 3) !== 0 || wram + EWRAM_SIZE + IWRAM_SIZE > limit) continue;
    if (romMatches(u32[i + 2])) return new GbaRam(memory, wram);
  }
  return null;
}
